/**
 * PATH: apps/quote/src/components/builder/scope-ag-grid/bravada-product-slot-bands.ts
 * FILE_ID: QB-AGGRID-BRAVADA-PRODUCT-SLOTS-001
 * PURPOSE: Four sub-bands (Primer / Basecoat / Topcoat / Accessories)
 *          that sit under the PRODUCTS parent band, mirroring Excel
 *          cols AB-AL exactly. The aggregate cols AM-AT live in the
 *          parent band's `columns` array (see bravada-pricing-bands.ts).
 *
 * EXCEL EVIDENCE (Q9525R9 master template):
 *   ┌───── Primer ──────┬───── Basecoat ────┬───── Topcoat ─────┬── Accessories ──┐
 *   │ AB     AC     AD  │ AE     AF     AG  │ AH     AI     AJ  │ AK         AL   │
 *   │ Unit | m²/u | T$  │ Unit | m²/u | T$  │ Unit | m²/u | T$  │ Unit Cost | T$  │
 *   └───────────────────┴───────────────────┴───────────────────┴─────────────────┘
 *
 * COLUMN ID CONVENTION:
 *   <slot>_unit_cost   (or accessories_unit_cost — verbose for clarity)
 *   <slot>_coverage    (m², LM/unit — primer/basecoat/topcoat only)
 *   <slot>_total_cost  (Excel "Total Cost $'s")
 *
 * Slot ids match the backend LineItemMaterial.SLOT_CHOICES:
 *   primer / basecoat / topcoat / other  (with 'other' → accessories
 *   prefix in field ids — same semantic, different display label).
 *
 * RELATED:
 *   - QB-AGGRID-MATERIAL-SLOTS-001 (bravada-material-slots.ts) —
 *     the pure resolvers that pluck slot values off
 *     QuoteLineItem.materials[].
 *   - QB-AGGRID-FMT-001 (scope-ag-grid-formatters.ts) — getComputedValue
 *     delegates slot-prefixed ids to getSlotFieldValue().
 */
import type { ColumnBand } from '../scope-sheet-grid/ScopeSheetGridTypes'
import { numCol, WIDTH } from './bravada-band-helpers'

// Shared visual treatment so the four sub-bands clearly belong to the
// PRODUCTS group. The parent band (in bravada-pricing-bands.ts) sets
// amber for the outer wrapper; sub-bands use a slightly lighter wash
// for visual hierarchy.
const SUB_BAND_BG = 'bg-amber-50/30 dark:bg-amber-950/20'
const SUB_BAND_TEXT = 'text-amber-700 dark:text-amber-300'

export const BRAVADA_PRIMER_BAND: ColumnBand = {
  id: 'product-primer',
  label: 'Primer',
  color: 'amber',
  bgClass: SUB_BAND_BG,
  textClass: SUB_BAND_TEXT,
  columns: [
    // Excel col AB
    numCol({
      id: 'primer_unit_cost',
      label: 'Unit cost',
      width: WIDTH.unitCost,
      computed: true,
    }),
    // Excel col AC
    numCol({
      id: 'primer_coverage',
      label: 'm², LM/unit',
      width: WIDTH.coverage,
      computed: true,
    }),
    // Excel col AD
    numCol({
      id: 'primer_total_cost',
      label: "Total Cost $'s",
      width: WIDTH.totalCost,
      computed: true,
    }),
  ],
}

export const BRAVADA_BASECOAT_BAND: ColumnBand = {
  id: 'product-basecoat',
  label: 'Basecoat',
  color: 'amber',
  bgClass: SUB_BAND_BG,
  textClass: SUB_BAND_TEXT,
  columns: [
    // Excel col AE
    numCol({
      id: 'basecoat_unit_cost',
      label: 'Unit cost',
      width: WIDTH.unitCost,
      computed: true,
    }),
    // Excel col AF
    numCol({
      id: 'basecoat_coverage',
      label: 'm², LM/unit',
      width: WIDTH.coverage,
      computed: true,
    }),
    // Excel col AG
    numCol({
      id: 'basecoat_total_cost',
      label: "Total Cost $'s",
      width: WIDTH.totalCost,
      computed: true,
    }),
  ],
}

export const BRAVADA_TOPCOAT_BAND: ColumnBand = {
  id: 'product-topcoat',
  label: 'Topcoat',
  color: 'amber',
  bgClass: SUB_BAND_BG,
  textClass: SUB_BAND_TEXT,
  columns: [
    // Excel col AH
    numCol({
      id: 'topcoat_unit_cost',
      label: 'Unit cost',
      width: WIDTH.unitCost,
      computed: true,
    }),
    // Excel col AI
    numCol({
      id: 'topcoat_coverage',
      label: 'm², LM/unit',
      width: WIDTH.coverage,
      computed: true,
    }),
    // Excel col AJ
    numCol({
      id: 'topcoat_total_cost',
      label: "Total Cost $'s",
      width: WIDTH.totalCost,
      computed: true,
    }),
  ],
}

export const BRAVADA_ACCESSORIES_BAND: ColumnBand = {
  id: 'product-accessories',
  label: 'Accessories / Other',
  color: 'amber',
  bgClass: SUB_BAND_BG,
  textClass: SUB_BAND_TEXT,
  columns: [
    // Excel col AK — Accessories has no coverage column (per-piece
    // pricing, not per-m²). Just unit cost + line total.
    numCol({
      id: 'accessories_unit_cost',
      label: 'Unit cost',
      width: WIDTH.unitCost,
      computed: true,
    }),
    // Excel col AL — total $ across whatever quantity of accessories
    // were entered (line total, not per-m²).
    numCol({
      id: 'accessories_total_cost',
      label: "Total Cost $'s",
      width: WIDTH.totalCost,
      computed: true,
    }),
  ],
}

/** All four product-slot sub-bands in Excel left-to-right order. */
export const BRAVADA_PRODUCT_SLOT_SUBBANDS: ColumnBand[] = [
  BRAVADA_PRIMER_BAND,
  BRAVADA_BASECOAT_BAND,
  BRAVADA_TOPCOAT_BAND,
  BRAVADA_ACCESSORIES_BAND,
]
